/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { IcrcmcpCore } from "../core.js";
import { encodeFormQuery, encodeSimple } from "../lib/encodings.js";
import * as M from "../lib/matchers.js";
import { compactMap } from "../lib/primitives.js";
import { safeParse } from "../lib/schemas.js";
import { RequestOptions } from "../lib/sdks.js";
import { pathToFunc } from "../lib/url.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { IcrcmcpError } from "../models/errors/icrcmcperror.js";
import * as errors from "../models/errors/index.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import * as models from "../models/index.js";
import * as operations from "../models/operations/index.js";
import { APICall, APIPromise } from "../types/async.js";
import { Result } from "../types/fp.js";

/**
 * Get a series of circulating supply values for a ledger
 *
 * @remarks
 * ### How is the data for this endpoint gathered?
 *
 * Circulating supply is calculated by the following formula:
 *
 * Total Supply = Sum of all mints - Sum of all burns
 * Circulating Supply = Total Supply - Sum of all balances of accounts to which tokens were minted before swap
 */
export function circulatingSupplyGetCirculatingSupplyApiV1LedgersLedgerCanisterIdCirculatingSupplyGet(
  client: IcrcmcpCore,
  request:
    operations.GetCirculatingSupplyApiV1LedgersLedgerCanisterIdCirculatingSupplyGetRequest,
  options?: RequestOptions,
): APIPromise<
  Result<
    models.SupplySeriesResponse,
    | errors.HTTPValidationError
    | IcrcmcpError
    | ResponseValidationError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
    | SDKValidationError
  >
> {
  return new APIPromise($do(
    client,
    request,
    options,
  ));
}

async function $do(
  client: IcrcmcpCore,
  request:
    operations.GetCirculatingSupplyApiV1LedgersLedgerCanisterIdCirculatingSupplyGetRequest,
  options?: RequestOptions,
): Promise<
  [
    Result<
      models.SupplySeriesResponse,
      | errors.HTTPValidationError
      | IcrcmcpError
      | ResponseValidationError
      | ConnectionError
      | RequestAbortedError
      | RequestTimeoutError
      | InvalidRequestError
      | UnexpectedClientError
      | SDKValidationError
    >,
    APICall,
  ]
> {
  const parsed = safeParse(
    request,
    (value) =>
      operations
        .GetCirculatingSupplyApiV1LedgersLedgerCanisterIdCirculatingSupplyGetRequest$outboundSchema
        .parse(value),
    "Input validation failed",
  );
  if (!parsed.ok) {
    return [parsed, { status: "invalid" }];
  }
  const payload = parsed.value;
  const body = null;

  const pathParams = {
    ledger_canister_id: encodeSimple(
      "ledger_canister_id",
      payload.ledger_canister_id,
      { explode: false, charEncoding: "percent" },
    ),
  };

  const path = pathToFunc(
    "/api/v1/ledgers/{ledger_canister_id}/circulating-supply",
  )(pathParams);

  const query = encodeFormQuery({
    "end": payload.end,
    "index": payload.index,
    "start": payload.start,
    "step": payload.step,
  });

  const headers = new Headers(compactMap({
    Accept: "application/json",
  }));

  const context = {
    options: client._options,
    baseURL: options?.serverURL ?? client._baseURL ?? "",
    operationID:
      "get_circulating_supply_api_v1_ledgers__ledger_canister_id__circulating_supply_get",
    oAuth2Scopes: [],

    resolvedSecurity: null,

    securitySource: null,
    retryConfig: options?.retries
      || client._options.retryConfig
      || { strategy: "none" },
    retryCodes: options?.retryCodes || ["429", "500", "502", "503", "504"],
  };

  const requestRes = client._createRequest(context, {
    method: "GET",
    baseURL: options?.serverURL,
    path: path,
    headers: headers,
    query: query,
    body: body,
    userAgent: client._options.userAgent,
    timeoutMs: options?.timeoutMs || client._options.timeoutMs || -1,
  }, options);
  if (!requestRes.ok) {
    return [requestRes, { status: "invalid" }];
  }
  const req = requestRes.value;

  const doResult = await client._do(req, {
    context,
    errorCodes: ["422", "4XX", "5XX"],
    retryConfig: context.retryConfig,
    retryCodes: context.retryCodes,
  });
  if (!doResult.ok) {
    return [doResult, { status: "request-error", request: req }];
  }
  const response = doResult.value;

  const responseFields = {
    HttpMeta: { Response: response, Request: req },
  };

  const [result] = await M.match<
    models.SupplySeriesResponse,
    | errors.HTTPValidationError
    | IcrcmcpError
    | ResponseValidationError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
    | SDKValidationError
  >(
    M.json(200, models.SupplySeriesResponse$inboundSchema),
    M.jsonErr(422, errors.HTTPValidationError$inboundSchema),
    M.fail("4XX"),
    M.fail("5XX"),
  )(response, req, { extraFields: responseFields });
  if (!result.ok) {
    return [result, { status: "complete", request: req, response }];
  }

  return [result, { status: "complete", request: req, response }];
}
